import moment from 'moment-timezone'
import {AssetGroup, PriceExtended, Prices, PricesExtended, Signals, TradingAccount} from '@bdswiss/mt4-connector'
import {accountSubtypes, accountTypes, companies, CompanyEnumIndices} from '@bdswiss/common-enums'
import {get} from 'lodash'
import {AccountDetails, AppTheme, SignalType} from '../types'
import {getCookie, setThemeCookie, setThemeLocalStorage} from '../utils/cookie-utils'
import {serverTimeZone} from '../lib/charting_library/utils'
import {I18n} from 'react-redux-i18n'
import React from 'react'
import {whitelabelConfig} from "../config";

const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const currentDateTime = new Date()
const weekDay = weekDays[currentDateTime.getDay()]

export const serverOffset = moment().tz(serverTimeZone).utcOffset() * 60

export function checkIfWeekends() {
  return weekDay === 'Saturday' || weekDay === 'Sunday'
}

export function removeServerOffset(time: number): number {
  return time - serverOffset
}

export function checkTradingHours(symbol: string, assets: Prices) {
  const asset = assets[symbol]

  const timeInTimezone = moment(currentDateTime).tz(serverTimeZone).format()
  const currentTime = moment(timeInTimezone, 'HH:mm a')

  if (checkIfWeekends()) {
    const sessionHours = asset.sessions[weekDay].split('-')
    const startTime = moment(sessionHours[0], 'HH:mm')
    const endTime = moment(sessionHours[1], 'HH:mm')

    if (currentTime.isBetween(startTime, endTime)) {
      return symbol
    } else {
      return 'BTCEUR'
    }
  } else {
    return symbol
  }
}

export function isNumber(num?: any): boolean {
  return num ? !isNaN(num) : num === 0
}

export function getServerTypeByAccountType(accountTypename: string) {
  return /demo/i.test(accountTypename) ? 'demo' : 'real'
}

export function notifyParentSignupRequired() {
  // console.log("Sending 'signup' to parent");
  window.parent.postMessage('signup', '*')
}

export function typeSelectorName(accountTypename: string): string {
  switch (true) {
    case /(?=.*demo)(?=.*forex)/i.test(accountTypename):
      return 'practiceAccount'
    case /forex/i.test(accountTypename):
      return 'realAccount'
    case /(?=.*demo)(?=.*bond)/i.test(accountTypename):
      return 'practiceBondsAccount'
    case /bond/i.test(accountTypename):
      return 'bondsAccount'
    case /cent/i.test(accountTypename):
      return 'centAccount'
    default:
      return 'realAccount'
  }
}

export function getAccountLabel(account: AccountDetails, locale: string) {
  const accountType = get(accountTypes, account.__typename)
  const accountSubtype = get(accountSubtypes, account.accountSubtype)
  let newLabel
  newLabel = accountType.localization.t(locale) || accountType.label
  if (accountSubtype) {
    newLabel = `${get(accountSubtype, 'label')} ${
      get(account, 'isDemo') ? `${I18n.t('demo')}` : `${I18n.t('live')}`
    }`
  }
  if (account.swapFree) {
    newLabel = `${I18n.t('islamic')} ${newLabel}`
  }
  return newLabel
}

export function filterTradeableAssets(assets: PricesExtended): PricesExtended {
  return Object.entries(assets).reduce(
    (acc, [symbol, asset]) => (!!asset.trade ? { ...acc, [symbol]: assets[symbol] } : acc),
    {}
  )
}

export function getTradeableAsset(
  assets: PricesExtended,
  symbol: string
): PriceExtended | undefined {
  return Object.values(assets).find(
    (asset) => (asset.normalizedSymbol === symbol || asset.symbol === symbol) && !!asset.trade
  )
}

export function getValidSymbol(prices: Prices, symbol: string) {
  const allSymbols = Object.keys(prices)
  if (prices[symbol]) {
    return symbol
  } else {
    const existingSymbol = allSymbols.find((sym) => sym.startsWith(symbol))
    return existingSymbol || allSymbols[0] || symbol
  }
}

export function getSymbolFromGroups(assetGroups: AssetGroup[], symbol: string) {
  const allSymbols = assetGroups
    .filter((grp) => grp.label !== 'popular')
    .reduce((acc, grp) => [...acc, ...grp.symbols], [] as string[])
  let existingSymbol = allSymbols.find((sym) => sym.startsWith(symbol))
  if (!existingSymbol && symbol?.length > 6) {
    existingSymbol = allSymbols.find((sym) => sym.startsWith(symbol.substr(0, 6)))
  }
  return existingSymbol || allSymbols[0] || symbol
}

export function setAppTheme(theme: AppTheme) {
  switch (theme) {
    case 'Light':
      document.body.classList.remove('theme-dark')
      document.body.classList.add('theme-light')
      break
    case 'Dark':
      document.body.classList.remove('theme-light')
      document.body.classList.add('theme-dark')
      break
  }
  setThemeCookie(theme)
  setThemeLocalStorage(theme)
}

export function getAvailableSymbols(symbols: string[], assets: PricesExtended): string[] {
  return symbols.filter((s) => assets[s] && !!assets[s].trade)
}

export function getAssetGroupSymbols(assetGroup: AssetGroup, assets: PricesExtended): string[] {
  const { symbols, securities } = assetGroup
  if (symbols && symbols.length) {
    return getAvailableSymbols(assetGroup.symbols, assets)
  } else if (securities) {
    return Object.values(assets).reduce(
      (acc, { symbol, security }) => (securities.includes(security) ? [...acc, symbol] : acc),
      [] as string[]
    )
  } else {
    return []
  }
}

export function getAvailableSymbolsGroups(
  assetGroups: AssetGroup[],
  assets: PricesExtended
): AssetGroup[] {
  return assetGroups.map((g) => ({ label: g.label, symbols: getAssetGroupSymbols(g, assets) }))
}

export function getSignalsType(): SignalType {
  switch(process.env.REACT_APP_SIGNALS_TYPE){
    case 'telegram': return SignalType.Telegram
    case 'simple': return SignalType.Simple
    case 'autochartist': return SignalType.Autochartist
    default: return SignalType.None
  }
}
export function getFilteredSignalsMap(assets: PricesExtended, signals: Signals) {
  const symbols = new Set(
    Object.values(assets)
      .filter((a) => !!a.trade)
      .map((a) => a.normalizedSymbol)
  )
  return Object.values(signals)
    .filter((s: any) => symbols.has(s.symbol))
    .reduce((acc, cur) => ({ ...acc, [cur.id]: cur }), {})
}

export function sendBackToDashboardPostMessage(): void {
  window.parent.postMessage({ type: 'backToDashboard' }, '*')
}

export function getCompanyName() {
  const company = companies[whitelabelConfig.whitelabel]
  return company.brandLabel;
}

export function isWhiteLabel() {
  const curUrl = document.referrer || window.location.href
  let whiteLabelState = false

  switch (true) {
    case new RegExp('skyground', 'i').test(curUrl):
      whiteLabelState = true
      break
    case new RegExp('equityflow', 'i').test(curUrl):
      whiteLabelState = true
      break
    case new RegExp('tauromarkets', 'i').test(curUrl):
      whiteLabelState = true
      break
  }
  return whiteLabelState
}

export function getCompanyLogo() {
  const theme = getCookie('theme') || 'Light'
  let loadingLogo = (
    <div className="loadingLogo">
      <img alt="Loading..." src={require('../images/loadericon.png').default} />
    </div>
  )
  switch (whitelabelConfig.whitelabel) {
    case CompanyEnumIndices.skyGround:
      loadingLogo = (
        <div className="loadingLogo">
          <img
            alt="Loading..."
            src={require(`../images/skyground-${theme === 'Dark' ? 'dark' : 'light'}.svg`).default}
          />
        </div>
      )
      break
    case CompanyEnumIndices.equityFlow:
      loadingLogo = (
        <div className="loadingLogo">
          <img
            alt="Loading..."
            src={require(`../images/equityflow-${theme === 'Dark' ? 'dark' : 'light'}.png`).default}
            />
        </div>
      )
      break
    case CompanyEnumIndices.lydian:
      loadingLogo = (
        <div className="loadingLogo">
          <img
            alt="Loading..."
            src={require(`../images/lydian-${theme === 'Dark' ? 'dark' : 'light'}.svg`).default}
          />
        </div>
      )
      break
    case CompanyEnumIndices.tauroMarkets:
      loadingLogo = (
        <div className="loadingLogo">
          <img
            alt="Loading..."
            src={require(`../images/tauromarkets-${theme === 'Dark' ? 'dark' : 'light'}.png`).default}
          />
        </div>
      )
      break
    case CompanyEnumIndices.bdSwissMauritius:
      loadingLogo = (
        <div className="loadingLogo">
          <img
            alt="Loading..."
            src={require(`../images/valutamarkets-${theme === 'Dark' ? 'dark' : 'light'}.png`).default}
          />
        </div>
      )
      break
  }
  return loadingLogo
}
export function getLeverageForSecurity(account: TradingAccount,asset: {percentage: number,symbol: string, marginMode: string}): number {
  let leverage  = (100 / asset.percentage)

  if(asset.marginMode === 'Forex'){
    leverage = account.leverage
  }
  if(whitelabelConfig.leverageOverrides && whitelabelConfig.leverageOverrides[asset.symbol]){
    leverage = whitelabelConfig.leverageOverrides[asset.symbol]
  }
  return leverage

}
